import { dataLayerPush } from '@utils/tracking';
import { useEffect, useRef } from 'react';
import { useRouter } from 'shared-routing';

const sendGAEvent = (scrollDepth: number) => {
  dataLayerPush({
    event: 'scrollDepth',
    scrollDepth,
  });
};

export const useScrollDepthTracker = () => {
  const router = useRouter();
  const maxPercentage = useRef(0);

  useEffect(() => {
    const handleRouteChange = () => {
      //resetting scroll depth on route change
      maxPercentage.current = 0;
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    const handleScroll = () => {
      const lastPercentage = Math.round(
        Math.min(1, (window.innerHeight + window.scrollY) / document.body.offsetHeight) * 100
      );
      if (
        lastPercentage > maxPercentage.current &&
        (lastPercentage === 25 ||
          lastPercentage === 50 ||
          lastPercentage === 75 ||
          lastPercentage === 100)
      ) {
        maxPercentage.current = lastPercentage;
        sendGAEvent(maxPercentage.current);
      }

      //This protects against users scrolling quickly -
      // sometimes when they scroll too fast events where lastPercentage are equal to 25,50,75, exactly are missed.
      if (
        lastPercentage > maxPercentage.current &&
        25 < lastPercentage &&
        lastPercentage < 50 &&
        maxPercentage.current < 25
      ) {
        maxPercentage.current = 25;
        sendGAEvent(maxPercentage.current);
      }

      if (
        lastPercentage > maxPercentage.current &&
        50 < lastPercentage &&
        lastPercentage < 75 &&
        maxPercentage.current < 50
      ) {
        maxPercentage.current = 50;
        sendGAEvent(maxPercentage.current);
      }

      if (
        lastPercentage > maxPercentage.current &&
        75 < lastPercentage &&
        lastPercentage < 100 &&
        maxPercentage.current < 75
      ) {
        maxPercentage.current = 75;
        sendGAEvent(maxPercentage.current);
      }

      // this covers the case where the page is short so we still want to release events for 25,50,75
      if (
        maxPercentage.current === 0 &&
        (lastPercentage > 25 || lastPercentage > 50 || lastPercentage > 75)
      ) {
        if (lastPercentage > 75) {
          maxPercentage.current = 75;
        }
        if (lastPercentage > 50) {
          maxPercentage.current = 25;
        }
        if (lastPercentage > 25) {
          maxPercentage.current = 25;
        }

        sendGAEvent(maxPercentage.current);
      }
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('load', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('load', handleScroll);
    };
  }, []);
};
